import React from "react";
import {
  ArrowUpRight,
  CalendarClock,
  KeyRound,
  Megaphone,
  MessageSquareText,
  Wrench,
} from "lucide-react";

import TemplateStatusBadge from "./TemplateStatusBadge";

const categoryConfig = {
  MARKETING: {
    icon: Megaphone,
    className: "border-[#b9dff0] bg-[#edf8fd] text-[#147db5] dark:border-[#28516a] dark:bg-[#142a36] dark:text-[#6fc5eb]",
  },
  UTILITY: {
    icon: Wrench,
    className: "border-[#afe0c5] bg-[#eefaf4] text-[#167a59] dark:border-[#285444] dark:bg-[#142d25] dark:text-[#67c9a1]",
  },
  AUTHENTICATION: {
    icon: KeyRound,
    className: "border-[#f2d2a7] bg-[#fff8eb] text-[#a96119] dark:border-[#5b472d] dark:bg-[#302719] dark:text-[#e2ad6c]",
  },
};

const qualityConfig = {
  GREEN: { label: "High quality", dot: "bg-[#20a777]" },
  HIGH: { label: "High quality", dot: "bg-[#20a777]" },
  YELLOW: { label: "Medium quality", dot: "bg-[#d49542]" },
  MEDIUM: { label: "Medium quality", dot: "bg-[#d49542]" },
  RED: { label: "Low quality", dot: "bg-[#c8645f]" },
  LOW: { label: "Low quality", dot: "bg-[#c8645f]" },
};

const getBodyPreview = (template) => {
  const components = Array.isArray(template?.components) ? template.components : [];
  const body = components.find((component) => component.type === "BODY");

  return body?.text || "Open the preview to review this template’s message and actions.";
};

const TemplateRow = ({ template, onPreview }) => {
  const category = categoryConfig[template.category] || {
    icon: MessageSquareText,
    className: "border-[#d5e1e6] bg-[#f6f9fa] text-[#607787] dark:border-[#334b59] dark:bg-[#172832] dark:text-[#91a9b6]",
  };
  const CategoryIcon = category.icon;
  const quality = qualityConfig[String(template.quality || "").toUpperCase()] || {
    label: "Not rated",
    dot: "bg-[#a2b0b7]",
  };

  return (
    <article className="group relative flex min-h-[245px] flex-col overflow-hidden rounded-[20px] border border-[#dbe6ea] bg-[#ffffff] p-4 shadow-[0_10px_30px_rgba(15,43,66,0.045)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#bad5df] hover:shadow-[0_18px_42px_rgba(15,43,66,0.09)] dark:border-[#2b4553] dark:bg-[#12242e] dark:hover:border-[#3c6578]">
      <div className="absolute inset-x-0 top-0 h-[2px] bg-[linear-gradient(90deg,#147db5_0%,#4db3da_45%,#20a777_100%)] opacity-55" />

      <div className="flex items-start justify-between gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-[13px] border ${category.className}`}>
          <CategoryIcon size={17} strokeWidth={1.75} />
        </div>
        <TemplateStatusBadge status={template.status} />
      </div>

      <div className="mt-4 min-w-0 flex-1">
        <div className="flex items-center gap-2 text-[8px] font-bold uppercase tracking-[0.12em] text-[#758b97] dark:text-[#78919e]">
          <span>{template.category || "Template"}</span>
          <span className="h-1 w-1 rounded-full bg-[#bfd0d7] dark:bg-[#3a5664]" />
          <span>{template.language || "—"}</span>
        </div>
        <h3 className="mt-2 truncate text-[14px] font-bold tracking-[-0.025em] text-[#173448] dark:text-[#edf6f9]" title={template.name}>
          {template.name}
        </h3>
        <p className="mt-2 line-clamp-2 text-[10px] font-medium leading-[1.7] text-[#768b96] dark:text-[#8099a6]">
          {getBodyPreview(template)}
        </p>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#e4ecef] pt-3.5 dark:border-[#29404d]">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[8px] font-bold text-[#5f7785] dark:text-[#8aa2ae]">
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${quality.dot}`} />
            {quality.label}
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 text-[8px] font-medium text-[#91a0a8] dark:text-[#6f8996]">
            <CalendarClock size={10} />
            Updated {template.updatedAt || "—"}
          </div>
        </div>

        <button
          type="button"
          onClick={() => onPreview?.(template)}
          title="View template"
          aria-label={`Preview ${template.name}`}
          className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-[#c7dce5] bg-[#f6fbfd] px-3 text-[9px] font-bold text-[#147db5] transition-all hover:border-[#8fc5da] hover:bg-[#edf8fd] dark:border-[#315364] dark:bg-[#162d38] dark:text-[#6fc5eb] dark:hover:border-[#427891]"
        >
          Preview
          <ArrowUpRight size={11} />
        </button>
      </div>
    </article>
  );
};

export default TemplateRow;
